/**
 * One line-icon set (Lucide-style paths, 1.75px stroke, rounded caps —
 * DESIGN.md §3.4). Icons are ALWAYS decorative (aria-hidden) and paired
 * with a visible text label; never icon-only controls.
 */
import type { JSX } from 'preact';

type IconProps = { size?: number };

function Svg({ size = 22, children }: IconProps & { children: JSX.Element | JSX.Element[] }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      stroke-width="1.75"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      class="icon"
    >
      {children}
    </svg>
  );
}

export const InboxIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M22 12h-6l-2 3h-4l-2-3H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </Svg>
);

export const BookingsIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </Svg>
);

/* T660 M6 (P0-23): CalendarIcon / DriversIcon / VehiclesIcon lived here for the
   rail's 'Coming soon' group. That group is gone, and with it their only
   importer, so the definitions went too - every icon in this file has exactly
   one live consumer again, which is the invariant that makes an unused export
   here read as a bug rather than as inventory. Restore from git if Phase 2
   builds the screens. */
export const RatesIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Svg>
);

export const PriceChangesIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 3h5v5" />
    <path d="M21 3l-7.5 7.5-4-4L3 13" />
    <path d="M3 17h6M3 21h10" />
  </Svg>
);

/**
 * ⚠️ HELD DELIBERATELY, WITH NO CALL SITE TODAY (T1749 M6). This was the
 * `Flagged quotes` rail row's mark; R6 removed the ROW and C2 ruled that the
 * `triage` ScreenId, its `App.tsx` switch arm and `screens/Triage.tsx` all stay
 * so re-enabling the queue is ONE row. Deleting this glyph would make that two.
 * ⇒ Delete it in the same change as `screens/Triage.tsx`, and not before.
 * Re-derive the call sites, never trust this note:
 *   /usr/bin/grep -rn 'FlagIcon' quote-helper/src quote-helper/test
 */
export const FlagIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.3 2 1.5 0 2.5-.4 3.2-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8 6.8 6.8 0 0 1-4.3 1.2C13 16 11 14 8 14a6 6 0 0 0-4 1.5" />
  </Svg>
);

/** Who can sign in (T928): two people - the operator roster, not one person. */
export const OperatorsIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);

/** Nursing home requests (T928): a building - work arriving from an
 *  organisation, which is what tells it apart from the web enquiries above. */
export const NursingHomeIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
    <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2" />
    <path d="M12 6v4M10 8h4" />
  </Svg>
);

export const LogOutIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </Svg>
);

export const PlaneIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M17.8 19.2 16 11l3.5-3.5a2.1 2.1 0 0 0-3-3L13 8 4.8 6.2a1 1 0 0 0-.9 1.7l4.6 3.1L6 14l-3-1-1 1 4 2 2 4 1-1-1-3 2.9-2.5 3.1 4.6a1 1 0 0 0 1.7-.9z" />
  </Svg>
);

export const GolfIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 18v-15" />
    <path d="M12 3l7 4-7 4" />
    <circle cx="12" cy="21" r="1" />
    <path d="M5 21c0-1.5 3-2.5 7-2.5s7 1 7 2.5" />
  </Svg>
);

export const RingsIcon = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="14" r="6" />
    <circle cx="15" cy="10" r="6" />
  </Svg>
);

export const MapIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);

export const BusIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
    <path d="M4 11h16M8 4v7M16 4v7" />
    <circle cx="8" cy="18" r="1" />
    <circle cx="16" cy="18" r="1" />
  </Svg>
);

/**
 * ⭐ T1749 M7 wave-3 (impeccable P1-3) — THE DISCLOSURE CARET. One chevron, drawn
 * pointing DOWN; the sheet rotates it when `aria-expanded` is true, so the glyph
 * and the announcement are one fact and there is no second icon to keep in step.
 * Decorative like every icon here (`aria-hidden`), because the control it sits in
 * already says what it does.
 */
export const ChevronIcon = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

